import { VyStack, VyTypography } from '@vy/ui-design-system';
import { PRICING_TEMPLATE_TYPES, type UserInputBillingInterval } from '@vy/types';
import { getBillingIntervalInMonths } from '@vy/services';
import { isNullOrUndefined, values } from '@vy/utils';
import { useEffect, type FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { userMessage } from '../../../../../../utils/vy-toasts';
import { useIsProductConfigurable } from '../../../create-plan-page-hooks/useIsProductConfigurable';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { VySelectField } from '../../../../../../design-system-adapters/forms';
import { BILLING_INTERVAL_OPTIONS, getBillingIntervalLabel } from './billing-interval-options';
import { getMonthIntervalLabel } from './month-interval-labels';

const CADENCE_INTERVALS = {
  MONTHLY: 1,
  BI_MONTHLY: 2,
  QUARTERLY: 3,
  EVERY_SIX_MONTHS: 6,
  YEARLY: 12,
} as const;

const CADENCE_OPTIONS = values(CADENCE_INTERVALS);

type CadenceBillingIntervalInputsProps = {
  fieldNamePrefix: 'usageProducts' | 'fixedProducts';
  productIndex: number;
  disabled?: boolean;
  syncMeterReset?: boolean;
};

export const CadenceBillingIntervalInputs: FC<CadenceBillingIntervalInputsProps> = ({
  fieldNamePrefix,
  productIndex,
  disabled,
  syncMeterReset = false,
}) => {
  const isProductConfigurable = useIsProductConfigurable();
  const { watch, setValue, getValues } = useFormContext<PlanInputForm, unknown, NewPlanForm>();

  const productPath = `${fieldNamePrefix}.${productIndex}` as const;
  const billingIntervalPath = `${productPath}.billingInterval` as const;
  const chargingCadencePath = `${productPath}.chargingCadence` as const;
  const accumulationIntervalPath = `${productPath}.accumulationInterval` as const;

  const product = watch(productPath);
  const pricingModel = product?.variants?.at(0)?.pricing?.pricingModel;

  const cadenceEditable =
    !disabled &&
    (isNullOrUndefined(pricingModel) || isProductConfigurable(pricingModel, PRICING_TEMPLATE_TYPES.CADENCE));

  const intervalEditable =
    !disabled &&
    (isNullOrUndefined(pricingModel) ||
      isProductConfigurable(pricingModel, PRICING_TEMPLATE_TYPES.BILLING_INTERVAL));

  const billingInterval = watch(billingIntervalPath);
  const numericBillingInterval = getBillingIntervalInMonths(billingInterval);

  const chargingCadence = watch(chargingCadencePath);

  useEffect(() => {
    if (isNullOrUndefined(chargingCadence)) return;

    if (chargingCadence > numericBillingInterval) {
      setValue(chargingCadencePath, numericBillingInterval);
      userMessage('warning', 'The period has been updated to match the billing interval');
    }
  }, [chargingCadence, chargingCadencePath, numericBillingInterval, setValue]);

  useEffect(() => {
    if (!syncMeterReset || isNullOrUndefined(chargingCadence)) return;

    if (getValues(accumulationIntervalPath) !== chargingCadence) {
      setValue(accumulationIntervalPath, chargingCadence);
    }
  }, [syncMeterReset, chargingCadence, accumulationIntervalPath, getValues, setValue]);

  return (
    <VyStack direction="row" data-field-span="full" sx={{ gap: 4, alignItems: 'flex-end' }}>
      <VySelectField<PlanInputForm, number>
        name={chargingCadencePath}
        label="Period"
        options={CADENCE_OPTIONS}
        getOptionLabel={getMonthIntervalLabel}
        getOptionDisabled={(option) => option > numericBillingInterval}
        disabled={!cadenceEditable}
        isOptionEqualToValue={(option, value) => option === value}
        disableClearable
        data-vy="plan-charge-drawer-cadence-select"
      />
      <VyTypography variant="body1" sx={{ color: 'text.disabled', mb: 1 }}>
        /
      </VyTypography>
      <VySelectField<PlanInputForm, UserInputBillingInterval>
        name={billingIntervalPath}
        label="Billing interval"
        options={BILLING_INTERVAL_OPTIONS}
        getOptionLabel={getBillingIntervalLabel}
        disabled={!intervalEditable}
        isOptionEqualToValue={(option, value) => option === value}
        disableClearable
        data-vy="plan-charge-drawer-billing-interval-select"
      />
    </VyStack>
  );
};

